/**
 * DIE PROJEKTLISTE IM GERAET — Kaltstart ohne vollen Load.
 *
 * ─── Der Anlass ─────────────────────────────────────────────────────────────
 * Die Seite stirbt auf Android oft (Kamera, App-Wechsel), und jeder Kaltstart
 * lud die volle Projektliste — 607 Projekte, rund 1,4 MB gzip; bei einem
 * Mitarbeiter im Feld 234 Starts im Monat, rund 330 MB. Jetzt liegt die Liste
 * je Nutzer in der IndexedDB: der Start zeigt sie sofort und gleicht danach
 * nur ab, was sich seit ihrem Stand geaendert hat (projektListeDelta.ts).
 *
 * ─── Was gespeichert wird ──────────────────────────────────────────────────
 *  - die Eintraege genau in der Form der Liste (Voll-Load), der Blob nur mit
 *    `metadata` — ein geoeffnetes Projekt kann ihn im Speicher ganz tragen,
 *    der Voll-Load liefert ihn nie ganz;
 *  - der WASSERSTAND (Serverzeit, `projekt_signale.geaendert_am`), die
 *    Abruf-Marke je Eintrag (fehlt sie, ist er LOKAL und wird beim naechsten
 *    Abgleich neu geladen), das Sichtungsbuch der Signalzeilen im Fenster der
 *    Ueberlappung und der Zaehler der logischen Uhr (projektListeDelta.ts) —
 *    nie die Geraeteuhr;
 *  - Format und die Spaltenliste der Abfrage (eine neue Spalte im Client
 *    heisst: alter Speicher taugt nicht, Voll-Load), der Nutzer, die Zeit des
 *    Speicherns (Geraeteuhr, nur fuer das Hoechstalter).
 *
 * ─── Hoechstens einen Tag ──────────────────────────────────────────────────
 * Liegt der letzte VOLL-Load mehr als 24 Stunden zurueck, wird der Speicher
 * nicht benutzt — gemessen am Voll-Load, nicht am Speichern (Aenderungen an
 * anderen Projekten schreiben ihn staendig neu). Auch im laufenden Betrieb
 * laedt das Nachholen dann voll (App.aktualisiereListe). Ein Signal, das
 * trotz allem fehlt (ein Triggerfehler wird zur Warnung, Nr. 175), haelt
 * einen Eintrag damit hoechstens einen Tag falsch.
 *
 * Jeder Eintrag traegt die Kennung des Listenauszugs (listenAuszug.ts): er
 * wird nie als Projekt gespeichert — auch nicht nach dem Weg durch die
 * IndexedDB.
 *
 * ─── Was NIE passiert ──────────────────────────────────────────────────────
 * Ein Fehler der IndexedDB (fehlt, gesperrte Website-Daten, privater Modus,
 * Quote voll, haengt) wirft nicht und haelt nichts auf: Lesen liefert dann
 * „nichts" und es wird wie bisher voll geladen. Jeder Zugriff hat eine
 * Zeitgrenze; alle stellen sich in EINE Kette (ein spaetes Schreiben kann kein
 * Loeschen beim Abmelden ueberholen).
 *
 * Geleert wird beim Abmelden, beim Sitzungsentzug (SIGNED_OUT) und — fuer
 * fremde Nutzer — beim Start eines anderen Nutzers (App.tsx).
 */

import { PROJECT_LIST_SELECT } from './queryColumns';
import { zeitstempelMikros, type Zeile } from './realtimeProjectSync';
import { alsListProject, type ListProject } from './listProject';
import {
    SICHTUNG_AUFBEWAHREN_MS, VOLL_LOAD_SPAETESTENS_MS, lesenAb, neuesSichtungsbuch,
    type AbrufMarken, type ListenStand, type Sichtung, type Sichtungsbuch,
} from './projektListeDelta';
import { LISTEN_AUSZUG_KENNUNG, istListenAuszug, markiereListenAuszug } from './listenAuszug';

/** Steigt, wenn sich die Form der Eintraege oder des Speichers aendert. */
export const PROJEKTLISTE_CACHE_FORMAT = 1;

/**
 * Liegt der letzte VOLL-Load laenger zurueck, wird der Speicher nicht benutzt
 * — gemessen am Voll-Load, nicht am Speichern (Codex-Review 21.10.2026, Runde
 * 2: Aenderungen an anderen Projekten hielten den Speicher sonst ewig frisch,
 * und ein verlorenes Signal blieb ewig falsch).
 */
export const CACHE_HOECHSTALTER_MS = VOLL_LOAD_SPAETESTENS_MS;

/** Mehr Eintraege als das werden nicht gespeichert (Groesse begrenzen). */
export const CACHE_HOECHSTENS_EINTRAEGE = 5000;

/** So lange darf ein Zugriff dauern, danach gilt er als gescheitert. */
export const CACHE_ZEITGRENZE_MS = 3000;

const DB_NAME = 'osia_projektliste_v1';
const STORE = 'listen';

const istObjekt = (x: unknown): x is Record<string, unknown> =>
    typeof x === 'object' && x !== null && !Array.isArray(x);

const istGanzzahl = (x: unknown): x is number => typeof x === 'number' && Number.isSafeInteger(x) && x >= 0;

// ═══════════════════════════════════════════════════════════════════════════
// DER GESPEICHERTE STAND — rein
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Sichtung im Speicher: [Signalzeile, Marke, geaendert_am, Projekt]. */
export type GespeicherteSichtung = [number, number, string, string];

export interface ProjektListeCacheStand {
    format: number;
    /** Die Abfrage der Liste, mit der die Eintraege entstanden (PROJECT_LIST_SELECT). */
    spalten: string;
    nutzerId: string;
    /** Geraeteuhr — wann gespeichert wurde (nur zur Auskunft). */
    gespeichertAmMs: number;
    /** Geraeteuhr — Beginn des letzten VOLL-Loads; daran haengt das Hoechstalter. */
    vollLoadAmMs: number;
    /** Serverzeit: bis hierher ist jedes Signal abgearbeitet. */
    wasserstand: string;
    eintraege: Zeile[];
    /** Abruf-Marke je Eintrag; fehlt sie, ist er LOKAL. */
    marken: Record<string, number>;
    /** Das Sichtungsbuch im Fenster der Ueberlappung. */
    sichtungen: GespeicherteSichtung[];
    /** Zaehler der logischen Uhr — groesser als jede gespeicherte Marke. */
    zaehler: number;
}

export type CacheVerworfen = 'keiner' | 'kaputt' | 'format' | 'spalten' | 'nutzer' | 'alt' | 'zukunft';

export interface CachePruefung {
    nutzerId: string;
    jetztMs: number;
    spalten?: string;
    hoechstalterMs?: number;
}

/**
 * Taugt der gelesene Speicher fuer diesen Start? Liefert den Stand oder den
 * Grund, warum nicht (dann: Voll-Load).
 */
export const pruefeCache = (
    roh: unknown,
    p: CachePruefung,
): { stand: ProjektListeCacheStand } | { verworfen: CacheVerworfen } => {
    if (roh === null || roh === undefined) return { verworfen: 'keiner' };
    if (!istObjekt(roh)) return { verworfen: 'kaputt' };
    if (roh.format !== PROJEKTLISTE_CACHE_FORMAT) return { verworfen: 'format' };
    if (roh.spalten !== (p.spalten ?? PROJECT_LIST_SELECT)) return { verworfen: 'spalten' };
    if (roh.nutzerId !== p.nutzerId) return { verworfen: 'nutzer' };
    if (typeof roh.gespeichertAmMs !== 'number' || !Number.isFinite(roh.gespeichertAmMs)) return { verworfen: 'kaputt' };
    const vollLoad = roh.vollLoadAmMs;
    if (typeof vollLoad !== 'number' || !Number.isFinite(vollLoad)) return { verworfen: 'kaputt' };
    // Mehr als eine Stunde „in der Zukunft": die Geraeteuhr sprang — das Alter
    // ist dann nicht zu beurteilen.
    if (vollLoad - p.jetztMs > 60 * 60 * 1000) return { verworfen: 'zukunft' };
    if (p.jetztMs - vollLoad > (p.hoechstalterMs ?? CACHE_HOECHSTALTER_MS)) return { verworfen: 'alt' };
    if (typeof roh.wasserstand !== 'string' || !roh.wasserstand) return { verworfen: 'kaputt' };
    if (!Array.isArray(roh.eintraege) || !roh.eintraege.every((e) => istObjekt(e) && typeof e.id === 'string')) {
        return { verworfen: 'kaputt' };
    }
    if (!istGanzzahl(roh.zaehler)) return { verworfen: 'kaputt' };
    const zaehler = roh.zaehler;
    if (!istObjekt(roh.marken) || !Object.values(roh.marken).every((m) => istGanzzahl(m) && m <= zaehler)) {
        return { verworfen: 'kaputt' };
    }
    if (!Array.isArray(roh.sichtungen) || !roh.sichtungen.every((s) => Array.isArray(s) && s.length === 4
        && istGanzzahl(s[0]) && istGanzzahl(s[1]) && s[1] <= zaehler && typeof s[2] === 'string' && typeof s[3] === 'string')) {
        return { verworfen: 'kaputt' };
    }
    return { stand: roh as unknown as ProjektListeCacheStand };
};

/**
 * Ein Eintrag in der Form, in der er gespeichert wird: wie der Voll-Load ihn
 * liefert — der Blob nur mit `metadata` und mit der Kennung des Listenauszugs
 * (listenAuszug.ts; sie ist aufzaehlbar und uebersteht die IndexedDB). Die
 * Eingabe bleibt unveraendert.
 */
export const alsCacheEintrag = (e: Zeile): Zeile => {
    const data = e.data;
    if (!istObjekt(data)) return e;
    const schluessel = Object.keys(data).filter((k) => k !== LISTEN_AUSZUG_KENNUNG);
    if (schluessel.length === 1 && schluessel[0] === 'metadata' && istListenAuszug(data)) return e;
    return { ...e, data: markiereListenAuszug({ metadata: istObjekt(data.metadata) ? data.metadata : { globalData: {} } }) };
};

export interface CacheStandEingabe {
    nutzerId: string;
    jetztMs: number;
    wasserstand: string | null;
    /** Beginn des letzten VOLL-Loads; ohne ihn wird nichts gespeichert. */
    vollLoadAmMs: number | null;
    eintraege: readonly Zeile[];
    /** Abruf-Marke eines Eintrag-Objekts; `undefined` = LOKAL. */
    markeVon: (eintrag: Zeile) => number | undefined;
    sichtungsbuch: Sichtungsbuch;
}

/** Baut den zu speichernden Stand — `null`, wenn nichts gespeichert werden soll. */
export const baueCacheStand = (e: CacheStandEingabe): ProjektListeCacheStand | null => {
    if (!e.wasserstand || e.vollLoadAmMs === null) return null;
    if (e.eintraege.length > CACHE_HOECHSTENS_EINTRAEGE) return null;
    const eintraege: Zeile[] = [];
    const marken: Record<string, number> = {};
    for (const eintrag of e.eintraege) {
        const id = eintrag.id;
        if (typeof id !== 'string') continue;
        eintraege.push(alsCacheEintrag(eintrag));
        const marke = e.markeVon(eintrag);
        if (marke !== undefined) marken[id] = marke;
    }
    // Nur Sichtungen, die ein naechster Abgleich noch lesen kann.
    const ab = zeitstempelMikros(lesenAb(e.wasserstand, SICHTUNG_AUFBEWAHREN_MS));
    const sichtungen: GespeicherteSichtung[] = [];
    for (const [zeile, s] of e.sichtungsbuch.zeilen) {
        const t = zeitstempelMikros(s.t);
        if (ab !== null && t !== null && t < ab) continue;
        sichtungen.push([zeile, s.n, s.t, s.p]);
    }
    return {
        format: PROJEKTLISTE_CACHE_FORMAT,
        spalten: PROJECT_LIST_SELECT,
        nutzerId: e.nutzerId,
        gespeichertAmMs: e.jetztMs,
        vollLoadAmMs: e.vollLoadAmMs,
        wasserstand: e.wasserstand,
        eintraege,
        marken,
        sichtungen,
        zaehler: e.sichtungsbuch.zaehler,
    };
};

/** Was ein gelesener Stand fuer den Start liefert. */
export interface HergestellteListe {
    /** Die Eintraege — jeder als Listenauszug gekennzeichnet. */
    eintraege: ListProject[];
    marken: AbrufMarken;
    sichtungsbuch: Sichtungsbuch;
    listenStand: ListenStand;
}

/**
 * Den gelesenen (geprueften) Stand fuer den Start herrichten: jeder Eintrag
 * traegt die Kennung des Listenauszugs (auch wenn ein aelterer Stand sie nicht
 * mitgespeichert haette — nie als Projekt speichern, listenAuszug.ts), die
 * Marken haengen an genau diesen Objekten, Sichtungsbuch und Uhr laufen
 * weiter.
 */
export const stelleCacheHer = (stand: ProjektListeCacheStand): HergestellteListe => {
    const marken: AbrufMarken = new WeakMap();
    for (const e of stand.eintraege) {
        if (istObjekt(e.data)) markiereListenAuszug(e.data);
        else e.data = markiereListenAuszug({ metadata: { globalData: {} } });
        const marke = stand.marken[e.id as string];
        if (marke !== undefined) marken.set(e, marke);
    }
    return {
        // Erst nach dem Kennzeichnen oben sind die Zeilen Listenform.
        eintraege: stand.eintraege.map(alsListProject),
        marken,
        sichtungsbuch: neuesSichtungsbuch(
            stand.zaehler,
            stand.sichtungen.map(([zeile, n, t, p]): [number, Sichtung] => [zeile, { n, t, p }]),
        ),
        listenStand: { wasserstand: stand.wasserstand, vollLoadAmMs: stand.vollLoadAmMs },
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// WANN GESPEICHERT WIRD — rein, Zeitgeber werden injiziert
// ═══════════════════════════════════════════════════════════════════════════

/** Nach einem Abgleich oder Voll-Load: bald (der Wasserstand ist gestiegen). */
export const SPEICHERN_DRINGEND_MS = 1000;
/**
 * Nach Realtime oder einer eigenen Aenderung: gesammelt. Jedes Schreiben
 * kopiert die ganze Liste (einige MB) — waehrend des Bearbeitens kommt nach
 * jedem Speichern eine Aenderung. Der wichtige Zeitpunkt ist ohnehin das
 * Verbergen des Tabs (danach stirbt die Seite), dort wird sofort geschrieben.
 */
export const SPEICHERN_RUHIG_MS = 2 * 60 * 1000;

export interface CacheSpeicherungOptionen {
    /** Schreibt den aktuellen Stand. Wirft nicht (der Speicher faengt alles). */
    schreiben: () => Promise<unknown>;
    jetzt?: () => number;
    schedule?: (fn: () => void, ms: number) => unknown;
    cancel?: (handle: unknown) => void;
}

export interface CacheSpeicherung {
    /** Es gibt etwas zu speichern; `dringend` nach einem Abgleich. */
    merke: (dringend: boolean) => void;
    /** Jetzt speichern, falls etwas ansteht (Tab wird verborgen). */
    sofort: () => void;
    /** Nichts mehr speichern (Abmelden, Nutzerwechsel). */
    abbauen: () => void;
}

/**
 * Speichert gesammelt: hoechstens ein Schreiben zugleich, ein Bedarf waehrend
 * des Schreibens wird danach eingeloest. Das Schreiben selbst kopiert die
 * ganze Liste (einige MB) — deshalb nicht nach jedem Realtime-Ereignis.
 */
export const erzeugeCacheSpeicherung = (opts: CacheSpeicherungOptionen): CacheSpeicherung => {
    const jetzt = opts.jetzt ?? (() => Date.now());
    const schedule = opts.schedule ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    const cancel = opts.cancel ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));

    let faelligAb: number | null = null;
    let timer: unknown = null;
    let timerAktiv = false;
    let laeuft = false;
    let abgebaut = false;

    const stoppen = () => {
        if (timerAktiv) cancel(timer);
        timer = null;
        timerAktiv = false;
    };

    const planen = () => {
        stoppen();
        if (abgebaut || faelligAb === null || laeuft) return;
        timerAktiv = true;
        timer = schedule(() => {
            timerAktiv = false;
            timer = null;
            void schreibenJetzt();
        }, Math.max(0, faelligAb - jetzt()));
    };

    const schreibenJetzt = async () => {
        if (abgebaut || laeuft || faelligAb === null) return;
        faelligAb = null;
        laeuft = true;
        try {
            await opts.schreiben();
        } catch {
            // Der Speicher wirft nicht; falls doch, bleibt es beim alten Stand.
        } finally {
            laeuft = false;
            planen();
        }
    };

    return {
        merke: (dringend) => {
            if (abgebaut) return;
            const ziel = jetzt() + (dringend ? SPEICHERN_DRINGEND_MS : SPEICHERN_RUHIG_MS);
            faelligAb = faelligAb === null ? ziel : Math.min(faelligAb, ziel);
            planen();
        },
        sofort: () => {
            if (abgebaut || faelligAb === null) return;
            faelligAb = jetzt();
            stoppen();
            void schreibenJetzt();
        },
        abbauen: () => {
            abgebaut = true;
            faelligAb = null;
            stoppen();
        },
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// DIE INDEXEDDB — fail-safe, mit Zeitgrenze, in einer Kette
// ═══════════════════════════════════════════════════════════════════════════

/** Liefert die IndexedDB — oder nichts. Darf werfen (gesperrte Website-Daten). */
export type IndexedDbQuelle = () => IDBFactory | null | undefined;

export interface ProjektListeSpeicher {
    /** Der gespeicherte Stand dieses Nutzers, UNGEPRUEFT (`pruefeCache`); `null` bei jedem Fehler. */
    lesen(nutzerId: string): Promise<unknown>;
    /** true, wenn der Stand fest liegt; false bei jedem Fehler (nie ein Wurf). */
    schreiben(stand: ProjektListeCacheStand): Promise<boolean>;
    /** Alle Listen weg (Abmelden, Sitzungsentzug). */
    alleLoeschen(): Promise<boolean>;
    /** Alle Listen ausser der dieses Nutzers weg (Nutzerwechsel im selben Geraet). */
    andereLoeschen(nutzerId: string): Promise<boolean>;
}

const ZEIT_UM = Symbol('projektliste-zeit-um');

const mitZeitgrenze = <T>(arbeit: Promise<T>, ms: number): Promise<T | typeof ZEIT_UM> => {
    let uhr: ReturnType<typeof setTimeout> | undefined;
    const wecker = new Promise<typeof ZEIT_UM>((resolve) => {
        uhr = setTimeout(() => resolve(ZEIT_UM), ms);
    });
    return Promise.race([arbeit, wecker]).finally(() => {
        if (uhr !== undefined) clearTimeout(uhr);
    });
};

/**
 * Fabrik des Speichers — die IndexedDB wird hereingereicht, damit er sich ohne
 * Browser pruefen laesst (das Projekt hat weder jsdom noch fake-indexeddb;
 * Muster assistentEntwurf.ts).
 */
export const erzeugeProjektListeSpeicher = (
    quelle: IndexedDbQuelle,
    zeitgrenzeMs: number = CACHE_ZEITGRENZE_MS,
): ProjektListeSpeicher => {
    let verbindung: Promise<IDBDatabase> | null = null;
    let kette: Promise<unknown> = Promise.resolve();

    const oeffnen = (): Promise<IDBDatabase> => {
        if (verbindung) return verbindung;
        const versuch = new Promise<IDBDatabase>((resolve, reject) => {
            let abgelaufen = false;
            const uhr = setTimeout(() => {
                abgelaufen = true;
                reject(new Error('IndexedDB antwortet nicht'));
            }, zeitgrenzeMs);
            const scheitern = (fehler: unknown) => {
                clearTimeout(uhr);
                reject(fehler);
            };
            let anfrage: IDBOpenDBRequest;
            try {
                const fabrik = quelle();
                if (!fabrik) {
                    scheitern(new Error('IndexedDB nicht verfuegbar'));
                    return;
                }
                anfrage = fabrik.open(DB_NAME, 1);
            } catch (fehler) {
                scheitern(fehler);
                return;
            }
            anfrage.onupgradeneeded = () => {
                const db = anfrage.result;
                if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
            };
            anfrage.onsuccess = () => {
                const db = anfrage.result;
                if (abgelaufen) {
                    db.close();
                    return;
                }
                clearTimeout(uhr);
                const vergessen = () => { if (verbindung === versuch) verbindung = null; };
                db.onclose = vergessen;
                db.onversionchange = () => { db.close(); vergessen(); };
                resolve(db);
            };
            anfrage.onerror = () => scheitern(anfrage.error ?? new Error('IndexedDB laesst sich nicht oeffnen'));
            anfrage.onblocked = () => { /* nur Upgrades blockieren; Fassung 1 aendert sich nicht */ };
        });
        verbindung = versuch;
        versuch.catch(() => { if (verbindung === versuch) verbindung = null; });
        return versuch;
    };

    /** Eine Transaktion; loest erst mit ihrem Abschluss auf, lehnt bei Fehler oder Abbruch ab. */
    const transaktion = <T>(
        modus: IDBTransactionMode,
        arbeit: (store: IDBObjectStore) => IDBRequest | void,
        ergebnis: (anfrage: IDBRequest | void) => T,
    ): Promise<T> => oeffnen().then((db) => new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, modus);
        const anfrage = arbeit(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(ergebnis(anfrage));
        tx.onerror = () => reject(tx.error ?? new Error('Projektliste: Transaktion gescheitert'));
        tx.onabort = () => reject(tx.error ?? new Error('Projektliste: Transaktion abgebrochen'));
    }));

    /** Hinten anstellen; jeder Zugriff loest auf — nie ein Wurf. */
    const anstellen = <T>(arbeit: () => Promise<T>, rueckfall: T): Promise<T> => {
        const lauf = kette
            .then(() => mitZeitgrenze(arbeit(), zeitgrenzeMs))
            .then((wert) => (wert === ZEIT_UM ? rueckfall : wert), () => rueckfall);
        kette = lauf;
        return lauf;
    };

    return {
        lesen: (nutzerId) => (!nutzerId ? Promise.resolve(null) : anstellen<unknown>(
            () => transaktion('readonly', (store) => store.get(nutzerId), (anfrage) => (anfrage ? (anfrage.result as unknown) ?? null : null)),
            null,
        )),
        schreiben: (stand) => {
            if (!stand?.nutzerId) return Promise.resolve(false);
            // Scheitert es (Quote voll), bleibt der vorige Stand liegen — er ist
            // in sich stimmig (aelterer Wasserstand, aeltere Eintraege), der
            // naechste Abgleich holt nur mehr nach.
            return anstellen(
                () => transaktion('readwrite', (store) => { store.put(stand, stand.nutzerId); }, () => true),
                false,
            );
        },
        alleLoeschen: () => anstellen(
            () => transaktion('readwrite', (store) => { store.clear(); }, () => true),
            false,
        ),
        andereLoeschen: (nutzerId) => anstellen(
            () => transaktion('readwrite', (store) => {
                const schluessel = store.getAllKeys();
                schluessel.onsuccess = () => {
                    for (const k of schluessel.result) if (k !== nutzerId) store.delete(k);
                };
            }, () => true),
            false,
        ),
    };
};

/** Die IndexedDB des Browsers; schon der Zugriff kann werfen (gesperrte Website-Daten). */
const browserIndexedDb: IndexedDbQuelle = () => (typeof indexedDB === 'undefined' ? null : indexedDB);

/** Der Speicher dieser Anwendung. */
export const projektListeSpeicher: ProjektListeSpeicher = erzeugeProjektListeSpeicher(browserIndexedDb);
